// ====================================================================================================
// Dynamic Template Engine for AI Asset Content Generator
// ====================================================================================================

/**
 * Dynamic Template Engine that generates content sections based on selected assets
 * Similar to the existing template system but fully dynamic based on asset types
 */

// Escape dynamic strings when used inside HTML attributes or inline HTML
function escapeHtmlAttr(str) {
    try {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/\"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    } catch (e) {
        return '';
    }
}


// ====================================================================================================
// Template 2: Single Media Display (for single image/video)
// ====================================================================================================
export const createSingleMediaTemplate = (data, idx) => {
    if (document.querySelector(`#dynamic_template_single_media_${idx}`)) return null;
    
    let templateDiv = document.createElement("div");
    templateDiv.className = "sxi_dynamic_template_div sxi_dynamic_single_media";
    templateDiv.id = `dynamic_template_single_media_${idx}`;
    
    // Determine media HTML based on type
    let mediaHTML = '';
    if (data.media.type === 'image') {
        mediaHTML = `<img src="${escapeHtmlAttr(data.media.url)}" alt="${escapeHtmlAttr(data.media.description || 'Media')}" class="sxi_dynamic_single_media_image">`;
    } else if (data.media.type === 'video') {
        mediaHTML = `
            <video controls class="sxi_dynamic_single_media_video">
                <source src="${escapeHtmlAttr(data.media.url)}" type="video/mp4">
                Your browser does not support the video tag.
            </video>
        `;
    }
    
    templateDiv.innerHTML = `
        <h2 class="sxi_dynamic_template_headline">${escapeHtmlAttr(data.headline || '')}</h2>
        <p class="sxi_dynamic_template_description">${escapeHtmlAttr(data.description || '')}</p>
        <div class="sxi_dynamic_single_media_container">
            ${mediaHTML}
            ${data.media.description ? `<div class="sxi_dynamic_single_media_caption">${escapeHtmlAttr(data.media.description)}</div>` : ''}
        </div>
    `;
    
    return templateDiv;
};

// ====================================================================================================
// Template 3: FAQ Section (for PDF content)
// ====================================================================================================
export const createFAQTemplate = (data, idx) => {
    // Skip DOM check in server-side rendering or when document is not available
    if (typeof document !== 'undefined' && document.querySelector(`#dynamic_template_faq_${idx}`)) return null;
    
    let templateDiv = document.createElement("div");
    templateDiv.className = "sxi_dynamic_template_div sxi_dynamic_faq";
    templateDiv.id = `dynamic_template_faq_${idx}`;
    
    templateDiv.innerHTML = `
        <div class="sxi-container" onclick="window.sxi_handleSectionClick('faq', ${idx})">
            <div class="sxi_dynamic_faq_container">
                <h2 class="sxi_dynamic_faq_heading">${data.headline}</h2>
                <p class="sxi_dynamic_faq_description">${data.description}</p>
                ${data.faq.map((faq, index) => `
                    <div class="sxi_dynamic_faq_item">
                        <div class="sxi_dynamic_faq_question" onclick="event.stopPropagation(); window.toggleFAQ(${idx}, ${index})">
                            ${faq.question}
                            <span class="sxi_dynamic_faq_toggle" id="toggle_${idx}_${index}">+</span>
                        </div>
                        <div class="sxi_dynamic_faq_answer" id="answer_${idx}_${index}" style="display: none;">
                            ${faq.answer}
                        </div>
                    </div>
                `).join('')}
            </div>
        </div>
    `;
    
    return templateDiv;
};

// ====================================================================================================
// Template 5: Card-based layout (for single media assets)
// ====================================================================================================
export const createTemplate5 = (data, idx) => {
    if (document.querySelector(`#dynamic_template_5_${idx}`)) return null;
    
    let templateDiv = document.createElement("div");
    templateDiv.className = "sxi_dynamic_template_div sxi_dynamic_template5_section";
    templateDiv.id = `dynamic_template_5_${idx}`;
    
    templateDiv.innerHTML = `
        <div class="sxi_dynamic_template5_container">
            <h2 class="sxi_dynamic_template5_heading">${data.heading}</h2>
            <div class="sxi_dynamic_template5_cards_wrapper">
                ${data.cards.map((card, index) => `
                    <div class="sxi_dynamic_template5_card">
                        <img src="${card.image}" class="sxi_dynamic_template5_image" alt="${card.title}" onerror="this.style.display='none'; console.error('Image failed to load:', this.src);" />
                        <h3 class="sxi_dynamic_template5_title">${card.title}</h3>
                        <p class="sxi_dynamic_template5_description">${card.description}</p>
                        <button class="sxi_dynamic_template5_button">Learn More</button>
                    </div>
                `).join('')}
            </div>
        </div>
    `;
    
    return templateDiv;
};

// ====================================================================================================
// Template 4: Contact Information (for contact assets)
// ====================================================================================================
export const createContactTemplate = (data, idx) => {
    if (document.querySelector(`#dynamic_template_contact_${idx}`)) return null;
    
    let templateDiv = document.createElement("div");
    templateDiv.className = "sxi_dynamic_template_div sxi_dynamic_contact";
    templateDiv.id = `dynamic_template_contact_${idx}`;
    
    templateDiv.innerHTML = `
        <div class="sxi_dynamic_contact_container">
            <h2 class="sxi_dynamic_contact_heading">${data.headline}</h2>
            <p class="sxi_dynamic_contact_description">${data.description}</p>
            <div class="sxi_dynamic_contact_info">
                ${data.contacts.map((contact, index) => `
                    <div class="sxi_dynamic_contact_item">
                        <div class="sxi_dynamic_contact_icon">📞</div>
                        <div class="sxi_dynamic_contact_details">
                            <h4>${contact.title}</h4>
                            <p>${contact.description}</p>
                            <a href="tel:${contact.contact}" class="sxi_dynamic_contact_link">${contact.contact}</a>
                        </div>
                    </div>
                `).join('')}
            </div>
        </div>
    `;
    
    return templateDiv;
};

// ====================================================================================================
// Template 5: Mixed Media Gallery (for multiple different asset types)
// ====================================================================================================
export const createMixedMediaTemplate = (data, idx) => {
    if (document.querySelector(`#dynamic_template_mixed_media_${idx}`)) return null;
    
    let templateDiv = document.createElement("div");
    templateDiv.className = "sxi_dynamic_template_div sxi_dynamic_mixed_media";
    templateDiv.id = `dynamic_template_mixed_media_${idx}`;
    
    templateDiv.innerHTML = `
        <h2 class="sxi_dynamic_template_headline">${data.headline}</h2>
        <p class="sxi_dynamic_template_description">${data.description}</p>
        
        <div class="sxi_dynamic_mixed_gallery">
            ${data.assets.map((asset, index) => {
                let mediaHTML = '';
                if (asset.type === 'image') {
                    mediaHTML = `<img src="${asset.url}" alt="${asset.description || 'Asset ' + (index + 1)}" class="sxi_dynamic_gallery_item_image">`;
                } else if (asset.type === 'video') {
                    mediaHTML = `
                        <video controls class="sxi_dynamic_gallery_item_video">
                            <source src="${asset.url}" type="video/mp4">
                            Your browser does not support the video tag.
                        </video>
                    `;
                } else if (asset.type === 'pdf') {
                    mediaHTML = `
                        <div class="sxi_dynamic_gallery_item_pdf">
                            <div class="sxi_dynamic_pdf_icon">📄</div>
                            <div class="sxi_dynamic_pdf_info">
                                <h4>${asset.title}</h4>
                                <p>${asset.description}</p>
                                <a href="${asset.url}" target="_blank" class="sxi_dynamic_pdf_link">View PDF</a>
                            </div>
                        </div>
                    `;
                } 
                // else if (asset.type === 'contact') {
                //     mediaHTML = `
                //         <div class="sxi_dynamic_gallery_item_contact">
                //             <div class="sxi_dynamic_contact_icon">📞</div>
                //             <div class="sxi_dynamic_contact_info">
                //                 <h4>${asset.title}</h4>
                //                 <p>${asset.description}</p>
                //                 <a href="tel:${asset.contact}" class="sxi_dynamic_contact_link">${asset.contact}</a>
                //             </div>
                //         </div>
                //     `;
                // }
                
                return `
                    <div class="sxi_dynamic_gallery_item sxi_dynamic_gallery_item-${asset.type}">
                        <div class="sxi_dynamic_gallery_media">
                            ${mediaHTML}
                        </div>
                        <div class="sxi_dynamic_gallery_caption">
                            <h4>${asset.title}</h4>
                            <p>${asset.description}</p>
                        </div>
                    </div>
                `;
            }).join('')}
        </div>
    `;
    
    return templateDiv;
};

// ====================================================================================================
// Helper Functions
// ====================================================================================================

// Carousel scroll function
window.sxi_dynamicScrollCarousel = function(direction, idx) {
    console.log('Carousel scroll called:', direction, idx);
    const carousel = document.getElementById(`sxi_dynamic_carousel_${idx}`);
    console.log('Carousel element:', carousel);
    
    if (!carousel) {
        console.error('Carousel not found:', `sxi_dynamic_carousel_${idx}`);
        return;
    }
    
    const slide = carousel.querySelector(".sxi_dynamic_carousel_slide");
    console.log('Slide element:', slide);
    
    if (!slide) {
        console.error('Slide not found in carousel');
        return;
    }
    
    const scrollAmount = slide.offsetWidth + 20; // 20px gap
    console.log('Scroll amount:', scrollAmount);
    
    carousel.scrollBy({
        left: direction * scrollAmount,
        behavior: "smooth",
    });
}; 

// Lightweight in-memory cache and request de-duplication for section assets
const __sectionAssetsCache = {
    css: new Map(), // sectionType -> string
    js: new Map()   // sectionType -> string
};
const __inflightRequests = new Map(); // key -> Promise<string>

// function buildSectionAssetUrls(sectionType, ext) {
//     // Prefer assets in public/ when available; fall back to src/ for dev
//     return [
//         `styles/section-templates/${sectionType}.${ext}`
//     ];
// }

async function fetchTextWithTimeout(url, { timeoutMs = 5000 } = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await fetch(url, { signal: controller.signal, cache: 'no-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return await res.text();
    } finally {
        clearTimeout(timeoutId);
    }
}

async function loadSectionAsset(sectionType, kind /* 'css' | 'js' */) {
    const cacheMap = kind === 'css' ? __sectionAssetsCache.css : __sectionAssetsCache.js;
    if (cacheMap.has(sectionType)) return cacheMap.get(sectionType);

    const inflightKey = `${kind}:${sectionType}`;
    if (__inflightRequests.has(inflightKey)) return __inflightRequests.get(inflightKey);

    const ext = kind === 'css' ? 'css' : 'js';
    const urls = buildSectionAssetUrls(sectionType, ext);
    console.log('Assets urls', urls);
    const p = (async () => {
        for (const url of urls) {
            try {
                const text = await fetchTextWithTimeout(url, { timeoutMs: 5000 });
                // Ignore SPA/HTML fallbacks erroneously served for asset URLs
                const lowered = (text || '').trim().toLowerCase();
                const looksLikeHtml = lowered.startsWith('<!doctype') || lowered.includes('<html');
                if (looksLikeHtml) {
                    // cache safe empty to avoid re-fetch loops
                    cacheMap.set(sectionType, '');
                    return '';
                }
                cacheMap.set(sectionType, text);
                return text;
            } catch (_) {
                // try next url
            }
        }
        // Nothing worked
        return '';
    })().finally(() => {
        __inflightRequests.delete(inflightKey);
    });

    __inflightRequests.set(inflightKey, p);
    return p;
}
///////////////////////////////////
function normalizeSectionType(sectionType) {
    if (!sectionType) return sectionType;
    // Convert underscore to hyphen for consistency with CSS file names
    return sectionType.replace(/_/g, '-');
}

function buildSectionAssetUrls(sectionType, ext) {
    // Normalize section type before building URLs
    const normalized = normalizeSectionType(sectionType);
    // Prefer assets in public/ when available; fall back to src/ for dev
    return [
        `styles/section-templates/${normalized}.${ext}`
    ];
}
async function getCurrentPageCSS(sectionType = null) {
    const detectedSectionType = sectionType || getCurrentSectionType();
    // Normalize section type before loading
    const normalizedType = normalizeSectionType(detectedSectionType);
    
    const loaded = await loadSectionAsset(normalizedType, 'css');
    if (typeof loaded === 'string' && loaded.length > 0) return loaded;
    // Fallback: Return minimal CSS based on normalized section type
    return getMinimalSectionCSS(normalizedType);
}

// Update getCurrentPageScript (around line 331)
async function getCurrentPageScript(sectionType = null) {
    const detectedSectionType = sectionType || getCurrentSectionType();
    // Normalize section type before loading
    const normalizedType = normalizeSectionType(detectedSectionType);
    const loaded = await loadSectionAsset(normalizedType, 'js');
    if (typeof loaded === 'string') return loaded;
    return '';
}



/////////////////////////////////

// // Helper function to get minimal CSS for VisualEditor (only essential section styles)
// async function getCurrentPageCSS() {
//     const sectionType = getCurrentSectionType();
//     const loaded = await loadSectionAsset(sectionType, 'css');
//     if (typeof loaded === 'string' && loaded.length > 0) return loaded;
//     // Fallback: Return minimal CSS based on section type
//     return getMinimalSectionCSS(sectionType);
// }

// // Helper function to get current page script
// async function getCurrentPageScript() {
//     const sectionType = getCurrentSectionType();
//     const loaded = await loadSectionAsset(sectionType, 'js');
//     if (typeof loaded === 'string') return loaded;
//     return '';
// }

// Helper function to get minimal CSS for each section type
export function getMinimalSectionCSS(sectionType) {
    const baseCSS = `
        /* Minimal reset for VisualEditor */
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            line-height: 1.6;
            color: #333;
            background: #f8f9fa;
        }
        
        /* Basic container styles */
        .sxi-container {
            width: 100%;
            max-width: 1200px;
            // margin: 40px auto;
            padding: 0 20px;
            position: relative;
        }
        
        .sxi-container * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
    `;
    
    switch (sectionType) {
        case 'faq':
            return baseCSS + `
                /* FAQ Section Styles */
                .sxi_dynamic_faq_container {
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 10px;
                }
                
                .sxi_dynamic_faq_heading {
                    font-size: 2.5rem;
                    font-weight: 700;
                    color: #1a1a1a;
                    margin-bottom: 20px;
                    text-align: center;
                    padding: 40px 40px 20px;
                }
                
                .sxi_dynamic_faq_description {
                    font-size: 1.1rem;
                    color: #555;
                    text-align: center;
                    margin-bottom: 40px;
                    padding: 0 40px;
                }
                
                .sxi_dynamic_faq_item {
                    margin-bottom: 16px;
                    background-color: #f3f6fb;
                    border-radius: 8px;
                    overflow: hidden;
                }
                
                .sxi_dynamic_faq_question {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px;
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #333;
                    cursor: pointer;
                    transition: background-color 0.3s ease;
                }
                
                .sxi_dynamic_faq_question:hover {
                    background-color: #e8f0fe;
                }
                
                .sxi_dynamic_faq_toggle {
                    font-size: 1.5rem;
                    font-weight: bold;
                    color: #007bff;
                    transition: transform 0.3s ease;
                    width: 30px;
                    height: 30px;
                    border: 2px solid #333;
                    border-radius: 50%;
                    text-align: center;
                    line-height: 26px;
                    background: white;
                    flex-shrink: 0;
                }
                
                .sxi_dynamic_faq_answer {
                    padding: 20px;
                    background-color: #f9fafc;
                    font-size: 1rem;
                    color: #666;
                    line-height: 1.6;
                    display: none;
                }
                
                .sxi_dynamic_faq_answer.show {
                    display: block;
                }
            `;
            
        case 'single-image':
            return baseCSS + `
                /* Single Image Section Styles */
                .sxi-section {
                    display: flex;
                    flex-wrap: wrap;
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
                }
                
                .sxi-text-content {
                    flex: 1;
                    min-width: 300px;
                    padding: 50px 40px;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                }
                
                .sxi-image-content {
                    flex: 1;
                    min-width: 300px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 40px;
                    background: #f8f9fa;
                }
                
                .sxi-title {
                    font-size: 2.5rem;
                    font-weight: 700;
                    color: #1a1a1a;
                    margin-bottom: 20px;
                    line-height: 1.2;
                }
                
                .sxi-description {
                    font-size: 1.1rem;
                    color: #555;
                    line-height: 1.7;
                }
                
                .sxi-image {
                    width: 100%;
                    max-width: 500px;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
                }
                
                .sxi-image img {
                    width: 100%;
                    display: block;
                    height: auto;
                }
            `;
            
        case 'single-video':
            return baseCSS + `
                /* Single Video Section Styles */
                .sxi-section {
                    display: flex;
                    flex-wrap: wrap;
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
                }
                
                .sxi-text-content {
                    flex: 1;
                    min-width: 300px;
                    padding: 50px 40px;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                }
                
                .sxi-image-content {
                    flex: 1;
                    min-width: 300px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 40px;
                    background: #f8f9fa;
                }
                
                .sxi-title {
                    font-size: 2.5rem;
                    font-weight: 700;
                    color: #1a1a1a;
                    margin-bottom: 20px;
                    line-height: 1.2;
                }
                
                .sxi-description {
                    font-size: 1.1rem;
                    color: #555;
                    line-height: 1.7;
                }
                
                .sxi-image iframe {
                    width: 100%;
                    height: 300px;
                    border-radius: 8px;
                }
            `;
            
        case 'image-carousel':
            return baseCSS + `
                /* Image Carousel Section Styles */
               
                
                .sxi_dynamic_section_header {
                    text-align: center;
                    padding: 40px 20px 20px;
                }
                
                .sxi_dynamic_template_headline {
                    font-size: 2.5rem;
                    font-weight: 700;
                    color: #1a1a1a;
                    margin-bottom: 15px;
                }
                
                .sxi_dynamic_template_description {
                    font-size: 1.1rem;
                    color: #555;
                    margin-bottom: 30px;
                }
                
                .sxi_dynamic_carousel_wrapper {
                    position: relative;
                    width: 100%;
                    margin: 20px 0;
                    padding: 0 20px;
                    max-width: 100%;
                }
                
                .sxi_dynamic_carousel_container {
                    display: flex;
                    overflow-x: auto;
                    scroll-behavior: smooth;
                    gap: 20px;
                    padding: 10px 0;
                    scroll-snap-type: x mandatory;
                    max-width: calc(100vw - 100px);
                    margin: 0 auto;
                }
                
                .sxi_dynamic_carousel_slide {
                    flex-shrink: 0;
                    scroll-snap-align: start;
                    position: relative;
                    width: 280px;
                    max-width: 280px;
                }
                
                .sxi_dynamic_media_wrapper {
                    position: relative;
                    width: 100%;
                    height: 250px;
                    border-radius: 10px;
                    overflow: hidden;
                }
                
                .sxi_dynamic_media_wrapper img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: 10px;
                }
                
                .sxi_dynamic_caption {
                    font-size: 0.9rem;
                    margin-top: 8px;
                    padding: 0 1rem;
                    text-align: center;
                    color: #666;
                }
                
                .sxi_dynamic_carousel_btn {
                    position: absolute;
                    top: calc(50% - 20px);
                    background-color: rgba(0, 0, 0, 0.5);
                    color: white;
                    border: none;
                    padding: 10px 12px;
                    cursor: pointer;
                    border-radius: 50%;
                    z-index: 10;
                    font-size: 1.2rem;
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .sxi_dynamic_carousel_btn:hover {
                    background-color: rgba(0, 0, 0, 0.7);
                }
                
                .sxi_dynamic_left {
                    left: 10px;
                }
                
                .sxi_dynamic_right {
                    right: 10px;
                }
                
                .sxi_dynamic_carousel_container::-webkit-scrollbar {
                    display: none;
                }
                
                .sxi_dynamic_section_footer {
                    text-align: center;
                    padding: 20px;
                    color: #666;
                    font-size: 0.9rem;
                }
                
                .text-muted {
                    color: #6c757d !important;
                }
            `;
            
        case 'video-carousel':
            return baseCSS + `
                /* Video Carousel Section Styles */
              
                
                .sxi_dynamic_section_header {
                    text-align: center;
                    padding: 40px 20px 20px;
                }
                
                .sxi_dynamic_template_headline {
                    font-size: 2.5rem;
                    font-weight: 700;
                    color: #1a1a1a;
                    margin-bottom: 15px;
                }
                
                .sxi_dynamic_template_description {
                    font-size: 1.1rem;
                    color: #555;
                    margin-bottom: 30px;
                }
                
                .sxi_dynamic_carousel_wrapper {
                    position: relative;
                    width: 100%;
                    margin: 20px 0;
                    padding: 0 20px;
                    max-width: 100%;
                }
                
                .sxi_dynamic_carousel_container {
                    display: flex;
                    overflow-x: auto;
                    scroll-behavior: smooth;
                    gap: 20px;
                    padding: 10px 0;
                    scroll-snap-type: x mandatory;
                    max-width: calc(100vw - 100px);
                    margin: 0 auto;
                }
                
                .sxi_dynamic_carousel_slide {
                    flex-shrink: 0;
                    scroll-snap-align: start;
                    position: relative;
                    width: 280px;
                    max-width: 280px;
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }
                
                .sxi_dynamic_media_wrapper {
                    position: relative;
                    width: 100%;
                    height: 200px;
                    overflow: hidden;
                }
                
                .sxi_dynamic_video_iframe {
                    width: 100%;
                    height: 100%;
                    border: none;
                    border-radius: 8px 8px 0 0;
                }
                
                .sxi_dynamic_caption {
                    padding: 15px;
                    background: #f8f9fa;
                    font-size: 14px;
                    color: #666;
                    text-align: center;
                    border-radius: 0 0 8px 8px;
                }
                
                .sxi_dynamic_carousel_btn {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    background-color: rgba(0, 0, 0, 0.7);
                    color: white;
                    border: none;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    font-size: 18px;
                    cursor: pointer;
                    z-index: 10;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .sxi_dynamic_carousel_btn:hover {
                    background-color: rgba(0, 0, 0, 0.9);
                }
                
                .sxi_dynamic_left {
                    left: 10px;
                }
                
                .sxi_dynamic_right {
                    right: 10px;
                }
                
                .sxi_dynamic_carousel_container::-webkit-scrollbar {
                    display: none;
                }
                
                .sxi_dynamic_section_footer {
                    text-align: center;
                    padding: 20px;
                    color: #666;
                    font-size: 0.9rem;
                }
                
                .text-muted {
                    color: #6c757d !important;
                }
            `;
            
        default:
            return baseCSS;
    }
}

// Helper function to get current section type
function getCurrentSectionType() {
    // Try to determine section type from various sources
    const sectionElement = document.querySelector('.sxi_dynamic_template_div');
    if (sectionElement) {
        if (sectionElement.classList.contains('sxi_dynamic_faq')) return 'faq';
        if (sectionElement.classList.contains('sxi_dynamic_single_image')) return 'single-image';
        if (sectionElement.classList.contains('sxi_dynamic_single_video')) return 'single-video';
        if (sectionElement.classList.contains('sxi_dynamic_image_carousel')) return 'image-carousel';
        if (sectionElement.classList.contains('sxi_dynamic_video_carousel')) return 'video-carousel';
    }
    
    // Fallback to session storage
    const sessionType = sessionStorage.getItem('current_section_type');
    if (sessionType) return sessionType;
    
    // Additional fallback - check for carousel elements in the HTML
    if (document.querySelector('.sxi_dynamic_carousel_container')) {
        if (document.querySelector('.sxi_dynamic_image_carousel')) return 'image-carousel';
        if (document.querySelector('.sxi_dynamic_video_carousel')) return 'video-carousel';
    }
    
    return 'faq';
}

// Helper function to get clean HTML content
function getCleanSectionHTML(sectionElement, sectionType) {
    // Clone the element to avoid modifying the live DOM
    const clonedElement = sectionElement.cloneNode(true);
    
    // Remove the sxi-container wrapper if it exists
    let innerHtml = clonedElement.outerHTML;
    if (clonedElement.classList.contains('sxi-container')) {
        innerHtml = clonedElement.innerHTML;
    }
    
    // Create a temporary div to parse the HTML and remove onclick attributes
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = innerHtml;
    
    // Remove onclick attributes from all elements EXCEPT carousel buttons and FAQ toggles
    tempDiv.querySelectorAll('[onclick]').forEach(el => {
        const isCarouselBtn = el.classList.contains('sxi_dynamic_carousel_btn');
        const isFAQQuestion = el.classList.contains('sxi_dynamic_faq_question');
        if (!isCarouselBtn && !isFAQQuestion) {
            el.removeAttribute('onclick');
        }
    });
    
    // For carousel sections, ensure the inline CSS is preserved
    if (sectionType === 'image_carousel' || sectionType === 'video_carousel') {
        // Check if there's already a style tag in the tempDiv
        let existingStyle = tempDiv.querySelector('style');
        if (!existingStyle) {
            // If no style tag exists, create one with the carousel CSS
            const styleTag = document.createElement('style');
            styleTag.innerHTML = getCarouselCSS(sectionType);
            tempDiv.insertBefore(styleTag, tempDiv.firstChild);
        }
    }
    
    return tempDiv.innerHTML;
}

// Helper function to get carousel CSS
function getCarouselCSS(sectionType) {
    if (sectionType === 'image_carousel') {
        return `
            /* Image Carousel CSS */
            
            .sxi_dynamic_section_header {
                text-align: center;
                padding: 40px 20px 20px;
            }
            
            .sxi_dynamic_template_headline {
                font-size: 2.5rem;
                font-weight: 700;
                color: #1a1a1a;
                margin-bottom: 15px;
            }
            
            .sxi_dynamic_template_description {
                font-size: 1.1rem;
                color: #555;
                margin-bottom: 30px;
            }
            
            .sxi_dynamic_carousel_wrapper {
                position: relative;
                width: 100%;
                margin: 20px 0;
                padding: 0 20px;
                max-width: 100%;
            }
            
            .sxi_dynamic_carousel_container {
                display: flex;
                overflow-x: auto;
                scroll-behavior: smooth;
                gap: 20px;
                padding: 10px 0;
                scroll-snap-type: x mandatory;
                max-width: calc(100vw - 100px);
                margin: 0 auto;
            }
            
            .sxi_dynamic_carousel_slide {
                flex-shrink: 0;
                scroll-snap-align: start;
                position: relative;
                width: 280px;
                max-width: 280px;
            }
            
            .sxi_dynamic_media_wrapper {
                position: relative;
                width: 100%;
                height: 250px;
                border-radius: 10px;
                overflow: hidden;
            }
            
            .sxi_dynamic_media_wrapper img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                border-radius: 10px;
            }
            
            .sxi_dynamic_caption {
                font-size: 0.9rem;
                margin-top: 8px;
                padding: 0 1rem;
                text-align: center;
                color: #666;
            }
            
            .sxi_dynamic_carousel_btn {
                position: absolute;
                top: calc(50% - 20px);
                background-color: rgba(0, 0, 0, 0.5);
                color: white;
                border: none;
                padding: 10px 12px;
                cursor: pointer;
                border-radius: 50%;
                z-index: 10;
                font-size: 1.2rem;
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            
            .sxi_dynamic_carousel_btn:hover {
                background-color: rgba(0, 0, 0, 0.7);
            }
            
            .sxi_dynamic_left {
                left: 10px;
            }
            
            .sxi_dynamic_right {
                right: 10px;
            }
            
            .sxi_dynamic_carousel_container::-webkit-scrollbar {
                display: none;
            }
            
            .sxi_dynamic_section_footer {
                text-align: center;
                padding: 20px;
                color: #666;
                font-size: 0.9rem;
            }
            
            .text-muted {
                color: #6c757d !important;
            }
        `;
    } else if (sectionType === 'video_carousel') {
        return `
            /* Video Carousel CSS */
            
            .sxi_dynamic_section_header {
                text-align: center;
                padding: 40px 20px 20px;
            }
            
            .sxi_dynamic_template_headline {
                font-size: 2.5rem;
                font-weight: 700;
                color: #1a1a1a;
                margin-bottom: 15px;
            }
            
            .sxi_dynamic_template_description {
                font-size: 1.1rem;
                color: #555;
                margin-bottom: 30px;
            }
            
            .sxi_dynamic_carousel_wrapper {
                position: relative;
                width: 100%;
                margin: 20px 0;
                padding: 0 20px;
                max-width: 100%;
            }
            
            .sxi_dynamic_carousel_container {
                display: flex;
                overflow-x: auto;
                scroll-behavior: smooth;
                gap: 20px;
                padding: 10px 0;
                scroll-snap-type: x mandatory;
                max-width: calc(100vw - 100px);
                margin: 0 auto;
            }
            
            .sxi_dynamic_carousel_slide {
                flex-shrink: 0;
                scroll-snap-align: start;
                position: relative;
                width: 280px;
                max-width: 280px;
                background: white;
                border-radius: 12px;
                overflow: hidden;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
            
            .sxi_dynamic_media_wrapper {
                position: relative;
                width: 100%;
                height: 200px;
                overflow: hidden;
            }
            
            .sxi_dynamic_video_iframe {
                width: 100%;
                height: 100%;
                border: none;
                border-radius: 8px 8px 0 0;
            }
            
            .sxi_dynamic_caption {
                padding: 15px;
                background: #f8f9fa;
                font-size: 14px;
                color: #666;
                text-align: center;
                border-radius: 0 0 8px 8px;
            }
            
            .sxi_dynamic_carousel_btn {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                background-color: rgba(0, 0, 0, 0.7);
                color: white;
                border: none;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                font-size: 18px;
                cursor: pointer;
                z-index: 10;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            
            .sxi_dynamic_carousel_btn:hover {
                background-color: rgba(0, 0, 0, 0.9);
            }
            
            .sxi_dynamic_left {
                left: 10px;
            }
            
            .sxi_dynamic_right {
                right: 10px;
            }
            
            .sxi_dynamic_carousel_container::-webkit-scrollbar {
                display: none;
            }
            
            .sxi_dynamic_section_footer {
                text-align: center;
                padding: 20px;
                color: #666;
                font-size: 0.9rem;
            }
            
            .text-muted {
                color: #6c757d !important;
            }
        `;
    }
    
    return '';
}

// Helper function to create complete HTML document
function isProbablyJSString(txt) {
    if (!txt || typeof txt !== 'string') return false;
    const lowered = txt.trim().toLowerCase();
    if (
        lowered.includes('<!doctype') ||
        lowered.includes('<html') ||
        lowered.includes('</html>') ||
        lowered.includes('<head') ||
        lowered.includes('<body') ||
        lowered.includes('</body>') ||
        lowered.includes('</head>') ||
        lowered.includes('</script>')
    ) return false;
    return true;
}

function createCompleteHTMLDocument(htmlContent, cssContent, sectionType, script) {
    // Generate a unique class for the body to ensure CSS isolation
    const uniqueBodyClass = `sxi-section-body-${sectionType.replace(/_/g, '-')}-${Date.now()}`;
    
    return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Edit ${sectionType.charAt(0).toUpperCase() + sectionType.slice(1)} Section</title>
            <style>
                /* Global reset for editor context */
                body {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }
                /* Isolated CSS for this section */
                ${cssContent}
            </style>
        </head>
        <body class="${uniqueBodyClass}">
            ${htmlContent}
            <script>
                ${isProbablyJSString(script) ? script : ''}
                
                ${sectionType === 'faq' ? `
                // Add toggleFAQ function and initialize event listeners for FAQ sections
                window.toggleFAQ = function(templateIdx, faqIdx) {
                    console.log('Toggle FAQ clicked - Template:', templateIdx, 'FAQ:', faqIdx);
                    const answer = document.getElementById('answer_' + templateIdx + '_' + faqIdx);
                    const toggle = document.getElementById('toggle_' + templateIdx + '_' + faqIdx);
                    
                    if (!answer || !toggle) {
                        console.error('FAQ elements not found:', answer, toggle);
                        return;
                    }
                    
                    // Close all other FAQs in this template
                    const template = document.getElementById('dynamic_template_faq_' + templateIdx);
                    if (template) {
                        const allAnswers = template.querySelectorAll('.sxi_dynamic_faq_answer');
                        const allToggles = template.querySelectorAll('.sxi_dynamic_faq_toggle');
                        
                        allAnswers.forEach((ans, index) => {
                            if (index !== faqIdx) {
                                ans.style.display = 'none';
                                if (allToggles[index]) {
                                    allToggles[index].textContent = '+';
                                }
                            }
                        });
                    }
                    
                    // Toggle current FAQ
                    const isOpen = answer.style.display === 'block';
                    answer.style.display = isOpen ? 'none' : 'block';
                    toggle.textContent = isOpen ? '+' : '−';
                    console.log('FAQ toggled:', isOpen ? 'closed' : 'opened');
                };
                
                console.log('FAQ toggle function initialized for VisualEditor');
                
                // Initialize event listeners for FAQ questions when DOM is ready
                function initFAQEventListeners() {
                    const questionElements = document.querySelectorAll('.sxi_dynamic_faq_question');
                    console.log('Found FAQ questions:', questionElements.length);
                    
                    questionElements.forEach((question, index) => {
                        // Remove the onclick attribute
                        question.removeAttribute('onclick');
                        
                        // Add event listener
                        question.addEventListener('click', function(e) {
                            e.stopPropagation();
                            
                            // Get the answer element (next sibling)
                            const answer = this.nextElementSibling;
                            const toggle = this.querySelector('.sxi_dynamic_faq_toggle');
                            
                            if (!answer || !toggle) {
                                console.log('FAQ elements not found:', answer, toggle);
                                return;
                            }
                            
                            // Find all answers and toggles in this FAQ template
                            const faqItems = this.closest('.sxi_dynamic_faq_container').querySelectorAll('.sxi_dynamic_faq_answer');
                            const toggles = this.closest('.sxi_dynamic_faq_container').querySelectorAll('.sxi_dynamic_faq_toggle');
                            
                            // Close all other FAQs
                            faqItems.forEach((ans, idx) => {
                                if (ans !== answer) {
                                    ans.style.display = 'none';
                                    if (toggles[idx] && toggles[idx] !== toggle) {
                                        toggles[idx].textContent = '+';
                                    }
                                }
                            });
                            
                            // Toggle current FAQ
                            const isOpen = answer.style.display === 'block';
                            answer.style.display = isOpen ? 'none' : 'block';
                            toggle.textContent = isOpen ? '+' : '−';
                            console.log('FAQ toggled (without IDs) - index:', index, 'isOpen:', !isOpen);
                        });
                    });
                    
                    console.log('FAQ event listeners attached');
                }
                
                // Initialize immediately if DOM is ready
                if (document.readyState === 'loading') {
                    document.addEventListener('DOMContentLoaded', initFAQEventListeners);
                } else {
                    initFAQEventListeners();
                }
                ` : ''}
            </script>
        </body>
        </html>
    `;
}

// Section click handler function
window.sxi_handleSectionClick = async function(sectionType, sectionIndex) {
    console.log('Section clicked:', sectionType, sectionIndex);
    
    // Find the clicked section element - handle different ID patterns
    let sectionElement;
    let selector;
    
    if (sectionType === 'image_carousel') {
        selector = `#sxi_dynamic_image_carousel_${sectionIndex}`;
        sectionElement = document.querySelector(selector);
    } else if (sectionType === 'video_carousel') {
        selector = `#dynamic_video_carousel_${sectionIndex}`;
        sectionElement = document.querySelector(selector);
    } else if (sectionType === 'faq') {
        selector = `#dynamic_template_faq_${sectionIndex}`;
        sectionElement = document.querySelector(selector);
    } else if (sectionType === 'single_video') {
        selector = `#dynamic_single_video_${sectionIndex}`;
        sectionElement = document.querySelector(selector);
    } else if (sectionType === 'single_image') {
        selector = `#sxi_dynamic_single_image_${sectionIndex}`;
        sectionElement = document.querySelector(selector);
    } else {
        selector = `#sxi_dynamic_${sectionType}_${sectionIndex}`;
        sectionElement = document.querySelector(selector);
    }
    
    console.log('Looking for element with selector:', selector);
    console.log('Found element:', sectionElement);
    
    if (!sectionElement) {
        console.error('Section element not found:', sectionType, sectionIndex, 'with selector:', selector);
        // Try to find any element with similar ID pattern
        const allElements = document.querySelectorAll('[id*="' + sectionType + '"]');
        console.log('Available elements with similar pattern:', allElements);
        return;
    }
    
    try {
        // Get the current CSS from the page (DynamicTemplates.css)
        let currentCSS = await getCurrentPageCSS(sectionType);
        console.log('Current page CSS for section type:', sectionType, 'loaded successfully');
        // Check if the section has inline CSS that we need to include
        const inlineStyle = sectionElement.querySelector('style');
        if (inlineStyle) {
            const inlineCSS = inlineStyle.innerHTML;
            console.log('Found inline CSS in section:', inlineCSS.substring(0, 100) + '...');
            // Combine the current CSS with inline CSS
            currentCSS = currentCSS + '\n' + inlineCSS;
        }
        
        // Get clean HTML content (without the sxi-container wrapper)
        const htmlContent = getCleanSectionHTML(sectionElement, sectionType);
        console.log('HTML content for section type:', sectionType, 'loaded successfully');

        // Get the script from the section element
        const script = await getCurrentPageScript(sectionType);

        // Create a complete HTML document with the current CSS
        const completeHtml = createCompleteHTMLDocument(htmlContent, currentCSS, sectionType, script);
        
        // Store the HTML content in sessionStorage for the VisualEditor
        sessionStorage.setItem('current_edit_div', completeHtml);
        // Store the section information in sessionStorage for the VisualEditor
        const uniqueId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('current_section_id', `${sectionType}_${sectionIndex}_${uniqueId}`);
        sessionStorage.setItem('current_section_name', `${sectionType.charAt(0).toUpperCase() + sectionType.slice(1)} Section ${sectionIndex + 1}`);
        sessionStorage.setItem('current_section_type', sectionType);
        // Set edit source to 'assets' when editing from Assets Section
        sessionStorage.setItem('edit_source', 'assets');
        sessionStorage.setItem('section_type','assets');
        // Navigate to the VisualEditor page
        window.location.href = '/editor';
        
    } catch (error) {
        console.error('Error preparing section for editing:', error);
        // Fallback to the old method if there's an error
        const htmlContent = sectionElement.outerHTML;
        sessionStorage.setItem('current_edit_div', htmlContent);
        sessionStorage.setItem('current_section_id', `${sectionType}_${sectionIndex}`);
        sessionStorage.setItem('current_section_name', `${sectionType.charAt(0).toUpperCase() + sectionType.slice(1)} Section ${sectionIndex + 1}`);
        sessionStorage.setItem('edit_source', 'assets');
        window.location.href = '/editor';
    }
};

// FAQ toggle function
window.toggleFAQ = function(templateIdx, faqIdx) {
    const answer = document.getElementById(`answer_${templateIdx}_${faqIdx}`);
    const toggle = document.getElementById(`toggle_${templateIdx}_${faqIdx}`);
    
    // Close all other FAQs in this template
    const template = document.getElementById(`dynamic_template_faq_${templateIdx}`);
    const allAnswers = template.querySelectorAll('.sxi_dynamic_faq_answer');
    const allToggles = template.querySelectorAll('.sxi_dynamic_faq_toggle');
    
    allAnswers.forEach((ans, index) => {
        if (index !== faqIdx) {
            ans.style.display = 'none';
            allToggles[index].textContent = '+';
        }
    });
    
    // Toggle current FAQ
    const isOpen = answer.style.display === 'block';
    answer.style.display = isOpen ? 'none' : 'block';
    toggle.textContent = isOpen ? '+' : '−';
};

// ====================================================================================================
// Dedicated Image Template Functions
// ====================================================================================================

/**
 * Create single image template
 * @param {Object} data - Template data
 * @param {number} idx - Template index
 * @returns {HTMLElement} Template element
 */
export const createSingleImageTemplate = (data, idx) => {
    // Skip DOM check in server-side rendering or when document is not available
    if (typeof document !== 'undefined' && document.querySelector(`#dynamic_single_image_${idx}`)) return null;
    
    console.log('Creating single image template with data:', data);
    console.log('Image URL being used:', data.image.url);
    
    let templateDiv = document.createElement("div");
    templateDiv.className = "sxi_dynamic_template_div sxi_dynamic_single_image";
    templateDiv.id = `sxi_dynamic_single_image_${idx}`;
    
    templateDiv.innerHTML = `
        <div class="sxi-container" onclick="window.sxi_handleSectionClick('single_image', ${idx})">
            <div class="sxi-section">
                <div class="sxi-text-content">
                    <h2 class="sxi-title">${data.headline}</h2>
                    <p class="sxi-description">${data.description}</p>
                </div>
                
                <div class="sxi-image-content">
                    <div class="sxi-image">
                        <img src="${escapeHtmlAttr(data.image.url)}" alt="${escapeHtmlAttr(data.image.description || 'Image')}" 
                             onload="console.log('Image loaded successfully:', this.src);"
                             onerror="console.error('Image failed to load:', this.src); this.style.display='none'; var cap=this.parentElement&&this.parentElement.nextElementSibling; if(cap) cap.style.display='block';">
                    </div>
                </div>
            </div>
        </div>
    `;
    
    return templateDiv;
};

/**
 * Create image carousel template (for 3+ images)
 * @param {Object} data - Template data
 * @param {number} idx - Template index
 * @returns {HTMLElement} Template element
 */
export const createImageCarouselTemplate = (data, idx) => {
    // Skip DOM check in server-side rendering or when document is not available
    if (typeof document !== 'undefined' && document.querySelector(`#dynamic_image_carousel_${idx}`)) return null;
    
    let templateDiv = document.createElement("div");
    templateDiv.className = "sxi_dynamic_template_div sxi_dynamic_image_carousel";
    templateDiv.id = `sxi_dynamic_image_carousel_${idx}`;
    
    templateDiv.innerHTML = `
        <style>
            /* Inline CSS for Image Carousel */
            .sxi_dynamic_image_carousel {
                // background: white;
                // border-radius: 12px;
                // overflow: hidden;
                // box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
                // margin: 30px 0;
            }
            
            .sxi_dynamic_section_header {
                text-align: center;
                padding: 40px 20px 20px;
            }
            
            .sxi_dynamic_template_headline {
                font-size: 2.5rem;
                font-weight: 700;
                color: #1a1a1a;
                margin-bottom: 15px;
            }
            
            .sxi_dynamic_template_description {
                font-size: 1.1rem;
                color: #555;
                margin-bottom: 30px;
            }
            
            .sxi_dynamic_carousel_wrapper {
                position: relative;
                width: 100%;
                margin: 20px 0;
                padding: 0 20px;
                max-width: 100%;
            }
            
            .sxi_dynamic_carousel_container {
                display: flex;
                overflow-x: auto;
                scroll-behavior: smooth;
                gap: 20px;
                padding: 10px 0;
                scroll-snap-type: x mandatory;
                max-width: calc(100vw - 100px);
                margin: 0 auto;
            }
            
            .sxi_dynamic_carousel_slide {
                flex-shrink: 0;
                scroll-snap-align: start;
                position: relative;
                width: 280px;
                max-width: 280px;
            }
            
            .sxi_dynamic_media_wrapper {
                position: relative;
                width: 100%;
                height: 250px;
                border-radius: 10px;
                overflow: hidden;
            }
            
            .sxi_dynamic_media_wrapper img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                border-radius: 10px;
            }
            
            .sxi_dynamic_caption {
                font-size: 0.9rem;
                margin-top: 8px;
                padding: 0 1rem;
                text-align: center;
                color: #666;
            }
            
            .sxi_dynamic_carousel_btn {
                position: absolute;
                top: calc(50% - 20px);
                background-color: rgba(0, 0, 0, 0.5);
                color: white;
                border: none;
                padding: 10px 12px;
                cursor: pointer;
                border-radius: 50%;
                z-index: 10;
                font-size: 1.2rem;
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            
            .sxi_dynamic_carousel_btn:hover {
                background-color: rgba(0, 0, 0, 0.7);
            }
            
            .sxi_dynamic_left {
                left: 10px;
            }
            
            .sxi_dynamic_right {
                right: 10px;
            }
            
            .sxi_dynamic_carousel_container::-webkit-scrollbar {
                display: none;
            }
            
            .sxi_dynamic_section_footer {
                text-align: center;
                padding: 20px;
                color: #666;
                font-size: 0.9rem;
            }
            
            .text-muted {
                color: #6c757d !important;
            }
        </style>
        <div class="sxi-container" onclick="window.sxi_handleSectionClick('image_carousel', ${idx})">
            <div class="sxi_dynamic_section_header">
                <h2 class="sxi_dynamic_template_headline">${escapeHtmlAttr(data.headline || '')}</h2>
                <p class="sxi_dynamic_template_description">${escapeHtmlAttr(data.description || '')}</p>
            </div>
            
            <div class="sxi_dynamic_carousel_wrapper">
                <button class="sxi_dynamic_carousel_btn sxi_dynamic_left" onclick="event.stopPropagation(); window.sxi_dynamicScrollCarousel('sxi_dynamic_carousel_${idx}', -1)">&#9664;</button>
                
                <div class="sxi_dynamic_carousel_container" id="sxi_dynamic_carousel_${idx}">
                    ${data.images.map((image, index) => `
                        <div class="sxi_dynamic_carousel_slide">
                            <div class="sxi_dynamic_media_wrapper">
                                <img src="${escapeHtmlAttr(image.url)}" alt="${escapeHtmlAttr(image.description || ('Image ' + (index + 1)))}" 
                                     onload="console.log('Carousel image loaded successfully:', this.src);"
                                     onerror="console.error('Carousel image failed to load:', this.src); this.style.display='none'; var cap=this.parentElement&&this.parentElement.nextElementSibling; if(cap) cap.style.display='block';">
                            </div>
                            <div class="sxi_dynamic_caption">${escapeHtmlAttr(image.description || '')}</div>
                        </div>
                    `).join("")}
                </div>
                
                <button class="sxi_dynamic_carousel_btn sxi_dynamic_right" onclick="event.stopPropagation(); window.sxi_dynamicScrollCarousel('sxi_dynamic_carousel_${idx}', 1)">&#9654;</button>
            </div>
        </div>
        <script>
            // Inline JavaScript for Image Carousel
            window.sxi_dynamicScrollCarousel = function(carouselId, direction) {
                console.log('Carousel scroll called with:', carouselId, direction);
                const carousel = document.getElementById(carouselId);
                if (!carousel) {
                    console.error('Carousel not found:', carouselId);
                    return;
                }
                
                const scrollAmount = 300;
                carousel.scrollBy({
                    left: direction * scrollAmount,
                    behavior: "smooth",
                });
            };
            
            // Initialize carousel immediately (for VisualEditor iframe)
            function initCarouselButtons() {
                const leftButtons = document.querySelectorAll('.sxi_dynamic_carousel_btn.sxi_dynamic_left');
                const rightButtons = document.querySelectorAll('.sxi_dynamic_carousel_btn.sxi_dynamic_right');
                
                console.log('Found carousel buttons - Left:', leftButtons.length, 'Right:', rightButtons.length);
                
                leftButtons.forEach(button => {
                    // Remove existing listeners to avoid duplicates
                    const newButton = button.cloneNode(true);
                    button.parentNode?.replaceChild(newButton, button);
                    
                    newButton.addEventListener('click', function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const carouselWrapper = this.closest('.sxi_dynamic_carousel_wrapper');
                        if (carouselWrapper) {
                            const carouselContainer = carouselWrapper.querySelector('.sxi_dynamic_carousel_container');
                            if (carouselContainer) {
                                let carouselId = carouselContainer.id;
                                // If ID is empty (was stripped during save), scroll directly
                                if (!carouselId) {
                                    const scrollAmount = 300;
                                    carouselContainer.scrollBy({
                                        left: -scrollAmount,
                                        behavior: "smooth"
                                    });
                                    return;
                                }
                                console.log('Left button clicked, carousel ID:', carouselId);
                                if (carouselId) {
                                    window.sxi_dynamicScrollCarousel(carouselId, -1);
                                }
                            }
                        }
                    });
                });
                
                rightButtons.forEach(button => {
                    // Remove existing listeners to avoid duplicates
                    const newButton = button.cloneNode(true);
                    button.parentNode?.replaceChild(newButton, button);
                    
                    newButton.addEventListener('click', function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const carouselWrapper = this.closest('.sxi_dynamic_carousel_wrapper');
                        if (carouselWrapper) {
                            const carouselContainer = carouselWrapper.querySelector('.sxi_dynamic_carousel_container');
                            if (carouselContainer) {
                                let carouselId = carouselContainer.id;
                                // If ID is empty (was stripped during save), scroll directly
                                if (!carouselId) {
                                    const scrollAmount = 300;
                                    carouselContainer.scrollBy({
                                        left: scrollAmount,
                                        behavior: "smooth"
                                    });
                                    return;
                                }
                                console.log('Right button clicked, carousel ID:', carouselId);
                                if (carouselId) {
                                    window.sxi_dynamicScrollCarousel(carouselId, 1);
                                }
                            }
                        }
                    });
                });
                
                console.log('Image carousel functionality initialized');
            }
            
            // Initialize immediately
            initCarouselButtons();
            
            // Also try on DOMContentLoaded as fallback
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', initCarouselButtons);
            }
            
            // Auto-slide functionality for VisualEditor - works without IDs - infinite loop
            function initAutoSlide() {
                const carousels = document.querySelectorAll('.sxi_dynamic_carousel_container');
                console.log('Setting up auto-slide for', carousels.length, 'carousels');
                
                carousels.forEach((carousel) => {
                    const carouselId = carousel.id;
                    
                    // Auto-slide every 5 seconds - infinite loop
                    const autoSlideInterval = setInterval(() => {
                        const scrollAmount = 300;
                        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
                        const currentScroll = carousel.scrollLeft;
                        
                        // If we're at or near the end, loop back to start
                        if (currentScroll >= maxScroll - 10) {
                            carousel.scrollTo({
                                left: 0,
                                behavior: "smooth"
                            });
                        } else {
                            // Otherwise, scroll forward
                            carousel.scrollBy({
                                left: scrollAmount,
                                behavior: "smooth"
                            });
                        }
                    }, 5000);
                    
                    // Store interval ID on the carousel element
                    carousel.dataset.autoSlideInterval = autoSlideInterval;
                    console.log('Auto-slide started for carousel (infinite loop):', carouselId || 'no ID');
                });
            }
            
            // Initialize auto-slide after a short delay
            setTimeout(initAutoSlide, 500);
        </script>
    `;
    
    return templateDiv;
};

// ====================================================================================================
// Dedicated Video Template Functions
// ====================================================================================================

/**
 * Create single video template
 * @param {Object} data - Template data
 * @param {number} idx - Template index
 * @returns {HTMLElement} Template element
 */
export const createSingleVideoTemplate = (data, idx) => {
    // Skip DOM check in server-side rendering or when document is not available
    if (typeof document !== 'undefined' && document.querySelector(`#dynamic_single_video_${idx}`)) return null;
    
    let templateDiv = document.createElement("div");
    templateDiv.className = "sxi_dynamic_template_div sxi_dynamic_single_video";
    templateDiv.id = `dynamic_single_video_${idx}`;
    
    // Sanitize injected values
    const safeHeadline = escapeHtmlAttr(data.headline || '');
    const safeDescription = escapeHtmlAttr(data.description || '');
    const safeVideoUrl = escapeHtmlAttr(data.video.url || '');
    const safeVideoDesc = escapeHtmlAttr(data.video.description || 'Video');

    templateDiv.innerHTML = `
        <div class="sxi-container" onclick="window.sxi_handleSectionClick('single_video', ${idx})">
            <div class="sxi-section">
                <div class="sxi-text-content">
                    <h2 class="sxi-title">${safeHeadline}</h2>
                    <p class="sxi-description">${safeDescription}</p>
                </div>
                <div class="sxi-image-content">
                    <div class="sxi-image">
                        <iframe 
                            src="${safeVideoUrl}" 
                            title="${safeVideoDesc}"
                            frameborder="0" 
                            allowfullscreen
                            style="width: 100%; height: 300px; border-radius: 8px;"
                            onload="console.log('Video iframe loaded:', this.src);"
                            onerror="console.error('Video failed to load:', this.src); this.style.display='none';">
                        </iframe>
                    </div>
                </div>
            </div>
        </div>
    `;
    
    return templateDiv;
};

/**
 * Create video carousel template (for 3+ videos)
 * @param {Object} data - Template data
 * @param {number} idx - Template index
 * @returns {HTMLElement} Template element
 */
export const createVideoCarouselTemplate = (data, idx) => {
    // Skip DOM check in server-side rendering or when document is not available
    if (typeof document !== 'undefined' && document.querySelector(`#dynamic_video_carousel_${idx}`)) return null;
    
    let templateDiv = document.createElement("div");
    templateDiv.className = "sxi_dynamic_template_div sxi_dynamic_video_carousel";
    templateDiv.id = `dynamic_video_carousel_${idx}`;
    
    templateDiv.innerHTML = `
        <style>
            /* Inline CSS for Video Carousel */
            .sxi_dynamic_video_carousel {

            }
            
            .sxi_dynamic_section_header {
                text-align: center;
                padding: 40px 20px 20px;
            }
            
            .sxi_dynamic_template_headline {
                font-size: 2.5rem;
                font-weight: 700;
                color: #1a1a1a;
                margin-bottom: 15px;
            }
            
            .sxi_dynamic_template_description {
                font-size: 1.1rem;
                color: #555;
                margin-bottom: 30px;
            }
            
            .sxi_dynamic_carousel_wrapper {
                position: relative;
                width: 100%;
                margin: 20px 0;
                padding: 0 20px;
                max-width: 100%;
            }
            
            .sxi_dynamic_carousel_container {
                display: flex;
                overflow-x: auto;
                scroll-behavior: smooth;
                gap: 20px;
                padding: 10px 0;
                scroll-snap-type: x mandatory;
                max-width: calc(100vw - 100px);
                margin: 0 auto;
            }
            
            .sxi_dynamic_carousel_slide {
                flex-shrink: 0;
                scroll-snap-align: start;
                position: relative;
                width: 280px;
                max-width: 280px;
                background: white;
                border-radius: 12px;
                overflow: hidden;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
            
            .sxi_dynamic_media_wrapper {
                position: relative;
                width: 100%;
                height: 200px;
                overflow: hidden;
            }
            
            .sxi_dynamic_video_iframe {
                width: 100%;
                height: 100%;
                border: none;
                border-radius: 8px 8px 0 0;
            }
            
            .sxi_dynamic_caption {
                padding: 15px;
                background: #f8f9fa;
                font-size: 14px;
                color: #666;
                text-align: center;
                border-radius: 0 0 8px 8px;
            }
            
            .sxi_dynamic_carousel_btn {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                background-color: rgba(0, 0, 0, 0.7);
                color: white;
                border: none;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                font-size: 18px;
                cursor: pointer;
                z-index: 10;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            
            .sxi_dynamic_carousel_btn:hover {
                background-color: rgba(0, 0, 0, 0.9);
            }
            
            .sxi_dynamic_left {
                left: 10px;
            }
            
            .sxi_dynamic_right {
                right: 10px;
            }
            
            .sxi_dynamic_carousel_container::-webkit-scrollbar {
                display: none;
            }
            
            .sxi_dynamic_section_footer {
                text-align: center;
                padding: 20px;
                color: #666;
                font-size: 0.9rem;
            }
            
            .text-muted {
                color: #6c757d !important;
            }
        </style>
        <div class="sxi-container" onclick="window.sxi_handleSectionClick('video_carousel', ${idx})">
            <div class="sxi_dynamic_section_header">
                <h2 class="sxi_dynamic_template_headline">${escapeHtmlAttr(data.headline || '')}</h2>
                <p class="sxi_dynamic_template_description">${escapeHtmlAttr(data.description || '')}</p>
            </div>
            
            <div class="sxi_dynamic_carousel_wrapper">
                <button class="sxi_dynamic_carousel_btn sxi_dynamic_left" onclick="event.stopPropagation(); window.sxi_dynamicScrollCarousel('sxi_dynamic_carousel_${idx}', -1)">&#9664;</button>
                
                <div class="sxi_dynamic_carousel_container" id="sxi_dynamic_carousel_${idx}">
                    ${data.videos.map((video, index) => `
                        <div class="sxi_dynamic_carousel_slide">
                            <div class="sxi_dynamic_media_wrapper">
                                <iframe 
                                    src="${escapeHtmlAttr(video.url)}" 
                                    title="${escapeHtmlAttr(video.title || video.description || ('Video ' + (index + 1)))}"
                                    frameborder="0" 
                                    allowfullscreen
                                    class="sxi_dynamic_video_iframe"
                                    onload="console.log('Carousel video iframe loaded:', this.src);"
                                    onerror="console.error('Carousel video failed to load:', this.src); this.style.display='none';">
                                </iframe>
                            </div>
                            <div class="sxi_dynamic_caption">
                                <strong>${escapeHtmlAttr(video.title || ('Video ' + (index + 1)))}</strong>
                                ${video.description ? `<br><small>${escapeHtmlAttr(video.description)}</small>` : ''}
                            </div>
                        </div>
                    `).join("")}
                </div>
                
                <button class="sxi_dynamic_carousel_btn sxi_dynamic_right" onclick="event.stopPropagation(); window.sxi_dynamicScrollCarousel('sxi_dynamic_carousel_${idx}', 1)">&#9654;</button>
            </div>
        </div>
        <script>
            // Inline JavaScript for Video Carousel
            window.sxi_dynamicScrollCarousel = function(carouselId, direction) {
                console.log('Carousel scroll called with:', carouselId, direction);
                const carousel = document.getElementById(carouselId);
                if (!carousel) {
                    console.error('Carousel not found:', carouselId);
                    return;
                }
                
                const scrollAmount = 300;
                carousel.scrollBy({
                    left: direction * scrollAmount,
                    behavior: "smooth",
                });
            };
            
            // Initialize carousel immediately (for VisualEditor iframe)
            function initCarouselButtons() {
                const leftButtons = document.querySelectorAll('.sxi_dynamic_carousel_btn.sxi_dynamic_left');
                const rightButtons = document.querySelectorAll('.sxi_dynamic_carousel_btn.sxi_dynamic_right');
                
                console.log('Found carousel buttons - Left:', leftButtons.length, 'Right:', rightButtons.length);
                
                leftButtons.forEach(button => {
                    // Remove existing listeners to avoid duplicates
                    const newButton = button.cloneNode(true);
                    button.parentNode?.replaceChild(newButton, button);
                    
                    newButton.addEventListener('click', function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const carouselWrapper = this.closest('.sxi_dynamic_carousel_wrapper');
                        if (carouselWrapper) {
                            const carouselContainer = carouselWrapper.querySelector('.sxi_dynamic_carousel_container');
                            if (carouselContainer) {
                                let carouselId = carouselContainer.id;
                                // If ID is empty (was stripped during save), scroll directly
                                if (!carouselId) {
                                    const scrollAmount = 300;
                                    carouselContainer.scrollBy({
                                        left: -scrollAmount,
                                        behavior: "smooth"
                                    });
                                    return;
                                }
                                console.log('Left button clicked, carousel ID:', carouselId);
                                if (carouselId) {
                                    window.sxi_dynamicScrollCarousel(carouselId, -1);
                                }
                            }
                        }
                    });
                });
                
                rightButtons.forEach(button => {
                    // Remove existing listeners to avoid duplicates
                    const newButton = button.cloneNode(true);
                    button.parentNode?.replaceChild(newButton, button);
                    
                    newButton.addEventListener('click', function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const carouselWrapper = this.closest('.sxi_dynamic_carousel_wrapper');
                        if (carouselWrapper) {
                            const carouselContainer = carouselWrapper.querySelector('.sxi_dynamic_carousel_container');
                            if (carouselContainer) {
                                let carouselId = carouselContainer.id;
                                // If ID is empty (was stripped during save), scroll directly
                                if (!carouselId) {
                                    const scrollAmount = 300;
                                    carouselContainer.scrollBy({
                                        left: scrollAmount,
                                        behavior: "smooth"
                                    });
                                    return;
                                }
                                console.log('Right button clicked, carousel ID:', carouselId);
                                if (carouselId) {
                                    window.sxi_dynamicScrollCarousel(carouselId, 1);
                                }
                            }
                        }
                    });
                });
                
                console.log('Video carousel functionality initialized');
            }
            
            // Initialize immediately
            initCarouselButtons();
            
            // Also try on DOMContentLoaded as fallback
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', initCarouselButtons);
            }
            
            // Auto-slide functionality for VisualEditor - works without IDs - infinite loop
            function initAutoSlide() {
                const carousels = document.querySelectorAll('.sxi_dynamic_carousel_container');
                console.log('Setting up auto-slide for', carousels.length, 'carousels');
                
                carousels.forEach((carousel) => {
                    const carouselId = carousel.id;
                    
                    // Auto-slide every 5 seconds - infinite loop
                    const autoSlideInterval = setInterval(() => {
                        const scrollAmount = 300;
                        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
                        const currentScroll = carousel.scrollLeft;
                        
                        // If we're at or near the end, loop back to start
                        if (currentScroll >= maxScroll - 10) {
                            carousel.scrollTo({
                                left: 0,
                                behavior: "smooth"
                            });
                        } else {
                            // Otherwise, scroll forward
                            carousel.scrollBy({
                                left: scrollAmount,
                                behavior: "smooth"
                            });
                        }
                    }, 5000);
                    
                    // Store interval ID on the carousel element
                    carousel.dataset.autoSlideInterval = autoSlideInterval;
                    console.log('Auto-slide started for carousel (infinite loop):', carouselId || 'no ID');
                });
            }
            
            // Initialize auto-slide after a short delay
            setTimeout(initAutoSlide, 500);
        </script>
    `;
    
    return templateDiv;
};

// ====================================================================================================
// Main Template Engine Function with Improved Logic
// ====================================================================================================

/**
 * Process assets and generate appropriate templates based on specific conditions
 * @param {Array} assets - Array of selected assets
 * @param {string} userPrompt - User's prompt for content generation
 * @param {string} aiResponse - AI generated content
 * @param {string} serverUrl - Server URL for asset file serving (optional)
 * @returns {Object} Object with templates and preview URLs
 */
export const processAssetsAndGenerateTemplates = (assets, page, userPrompt, aiResponse, serverUrl = null) => {
    const templates = [];
    const previewUrls = [];
    console.log("==========================================================");
    console.log('Page:', page);
    console.log(aiResponse);
    console.log("AiResponse=====================================================")
    // Get the correct base URL for asset serving
    const getBaseUrl = () => {
        if (serverUrl) return serverUrl;
        if (typeof window !== 'undefined') {
            // In browser, try to detect if we're in development
            if (window.location.origin.includes('3000')) {
                return 'http://localhost:9000'; // Default server port
            }
            return window.location.origin;
        }
        return 'http://localhost:9000'; // Fallback
    };
    
    const baseUrl = getBaseUrl();
    console.log('Using base URL for assets:', baseUrl);
    
    // Group assets by type
    const assetsByType = {
        images: assets.filter(asset => asset.type === 'Image'),
        videos: assets.filter(asset => 
            asset.type === 'Video' || 
            asset.type === 'Video Link' || 
            (asset.url && (asset.url.includes('youtube.com') || asset.url.includes('youtu.be'))) ||
            (asset.fileUrl && (asset.fileUrl.includes('youtube.com') || asset.fileUrl.includes('youtu.be'))) ||
            (asset.videoUrl && (asset.videoUrl.includes('youtube.com') || asset.videoUrl.includes('youtu.be')))
        ),
        pdfs: assets.filter(asset => asset.type === 'PDF'),
        contacts: assets.filter(asset => asset.type === 'Contact')
    };
    
    // Debug logging
    console.log('All assets:', assets);
    console.log('Asset types found:', [...new Set(assets.map(asset => asset.type))]);
    console.log('Assets by type:', assetsByType);
    console.log('Video assets:', assetsByType.videos);
    console.log('Video assets count:', assetsByType.videos.length);
    
    let templateIndex = 0;
    let sectionIndex = 1;
    
    // Template Logic Based on Conditions
    
    // 1. Image Templates
    if (assetsByType.images.length > 0) {
        console.log('Processing images:', assetsByType.images.length);
        console.log('First image asset:', assetsByType.images[0]);
        console.log('First image asset keys:', Object.keys(assetsByType.images[0]));
        
        // Always create single image template for any number of images
        if (assetsByType.images.length >= 1) {
            // Single image - generate multiple sections
            console.log('Creating single image template with AI response:', aiResponse);
            console.log('AI Sections for single image:', aiResponse?.sections);
            console.log('Selected image:', assetsByType.images[0]);
            
            // Create single image template (only once)
            console.log('Image asset for single template:', assetsByType.images[0]);
            console.log('Available fields:', Object.keys(assetsByType.images[0]));
            
            let imageUrl = assetsByType.images[0].publicUrl || assetsByType.images[0].fileUrl || assetsByType.images[0].imageUrl || assetsByType.images[0].url;
            
            // If still no URL, try to construct it from asset ID
            if (!imageUrl && assetsByType.images[0]._id) {
                imageUrl = `${baseUrl}/api/assets/file/${assetsByType.images[0]._id}?shop_domain=${assetsByType.images[0].shop_domain || 'default'}`;
            }
            
            // Ensure absolute URL
            if (imageUrl && !imageUrl.startsWith('http')) {
                if (imageUrl.startsWith('/')) {
                    imageUrl = `${baseUrl}${imageUrl}`;
                } else {
                    imageUrl = `${baseUrl}/${imageUrl}`;
                }
            }
            
            console.log('Creating single image template with URL:', imageUrl);
            console.log('Final image URL:', imageUrl);
            
            // Use first AI section if available, otherwise use fallback
            const firstSection = aiResponse.sections && Array.isArray(aiResponse.sections) && aiResponse.sections.length > 0 
                ? aiResponse.sections[0] 
                : null;
            
            const template = createSingleImageTemplate({
                headline: assetsByType.images[0].title || "Featured Image",
                description: assetsByType.images[0].description || "Check out this amazing content",
                image: {
                    url: imageUrl,
                    description: assetsByType.images[0].description
                }
            }, templateIndex);
            
            templates.push(template);
            previewUrls.push({
                section_id: `single_image_${templateIndex}`,
                section_name: "Single Image Display",
                section_type: "image",
                template: template,
                preview_url: `#section_${sectionIndex}_single_image`
            });
            templateIndex++;
            sectionIndex++;
        }
        
        // Create individual single image sections for each image
        if (assetsByType.images.length > 1) {
            console.log('Creating individual single image sections for', assetsByType.images.length, 'images');
            
            // Create single image sections for each image (skip the first one as it's already created above)
            for (let i = 1; i < assetsByType.images.length; i++) {
                const img = assetsByType.images[i];
                let imageUrl = img.publicUrl || img.fileUrl || img.imageUrl || img.url;
                
                // If still no URL, try to construct it from asset ID
                if (!imageUrl && img._id) {
                    imageUrl = `${baseUrl}/api/assets/file/${img._id}?shop_domain=${img.shop_domain || 'default'}`;
                }
                
                // Ensure absolute URL
                if (imageUrl && !imageUrl.startsWith('http')) {
                    if (imageUrl.startsWith('/')) {
                        imageUrl = `${baseUrl}${imageUrl}`;
                    } else {
                        imageUrl = `${baseUrl}/${imageUrl}`;
                    }
                }
                
                console.log(`Creating single image section ${i + 1} with URL:`, imageUrl);
                
                const template = createSingleImageTemplate({
                    headline: img.title || `Image ${i + 1}`,
                    description: img.description || "Check out this amazing content",
                    image: {
                        url: imageUrl,
                        description: img.description
                    }
                }, templateIndex);
                
                templates.push(template);
                previewUrls.push({
                    section_id: `single_image_${templateIndex}`,
                    section_name: `Single Image ${i + 1}`,
                    section_type: "image",
                    template: template,
                    preview_url: `#section_${sectionIndex}_single_image`
                });
                templateIndex++;
                sectionIndex++;
            }
        }
        
        // Also create carousel template for 4+ images (limited to 6 items)
        if (assetsByType.images.length >= 4) {
            // Image carousel template (4+ images, limited to 6)
            const carouselImages = assetsByType.images.slice(0, 6); // Limit to 6 items
            console.log('Creating image carousel for', carouselImages.length, 'images (limited from', assetsByType.images.length, ')');
            console.log('Image carousel assets:', carouselImages);
            
            const template = createImageCarouselTemplate({
                headline: aiResponse.headline || "Image Gallery",
                description: aiResponse.description || "Browse through our collection",
                images: carouselImages.map((img, index) => {
                    let imageUrl = img.publicUrl || img.fileUrl || img.imageUrl || img.url;
                    
                    // If still no URL, try to construct it from asset ID
                    if (!imageUrl && img._id) {
                        imageUrl = `${baseUrl}/api/assets/file/${img._id}?shop_domain=${img.shop_domain || 'default'}`;
                    }
                    
                    // Ensure absolute URL
                    if (imageUrl && !imageUrl.startsWith('http')) {
                        if (imageUrl.startsWith('/')) {
                            imageUrl = `${baseUrl}${imageUrl}`;
                        } else {
                            imageUrl = `${baseUrl}/${imageUrl}`;
                        }
                    }
                    
                    console.log(`Carousel image ${index + 1} URL:`, imageUrl);
                    
                    return {
                        url: imageUrl,
                        description: img.description
                    };
                })
            }, templateIndex);
            
            templates.push(template);
            previewUrls.push({
                section_id: `image_carousel_${templateIndex}`,
                section_name: "Image Carousel",
                section_type: "image_carousel",
                template: template,
                preview_url: `#section_${sectionIndex}_image_carousel`
            });
            templateIndex++;
            sectionIndex++;
        }
    }
    
    // 2. Video Templates
    if (assetsByType.videos.length > 0) {
        console.log('Creating video templates for', assetsByType.videos.length, 'videos');
        
        // Always create single video template for any number of videos
        if (assetsByType.videos.length >= 1) {
            // Single video - generate multiple sections
            console.log('Creating single video template with AI response:', aiResponse);
            console.log('AI Sections for single video:', aiResponse?.sections);
            console.log('Selected video:', assetsByType.videos[0]);
            
            console.log('Video asset for single template:', assetsByType.videos[0]);
            console.log('Video asset keys:', Object.keys(assetsByType.videos[0]));
            
            // Check multiple possible URL fields
            let videoUrl = assetsByType.videos[0].publicUrl || assetsByType.videos[0].fileUrl || assetsByType.videos[0].url || assetsByType.videos[0].videoUrl || assetsByType.videos[0].filePath || assetsByType.videos[0].imageUrl;
            
            // If we have a direct URL (like YouTube), use it directly
            if (videoUrl && (videoUrl.startsWith('http') || videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'))) {
                // Use the URL directly for external links
                console.log('Using direct video URL for single template:', videoUrl);
            } else if (!videoUrl && assetsByType.videos[0]._id) {
                // If no URL found, try to construct it from asset ID
                videoUrl = `${baseUrl}/api/assets/file/${assetsByType.videos[0]._id}?shop_domain=${assetsByType.videos[0].shop_domain || 'default'}`;
            } else if (videoUrl && !videoUrl.startsWith('http')) {
                // Ensure absolute URL for relative paths
                if (videoUrl.startsWith('/')) {
                    videoUrl = `${baseUrl}${videoUrl}`;
                } else {
                    videoUrl = `${baseUrl}/${videoUrl}`;
                }
            }
            
            console.log('Single video - Original URL:', videoUrl);
            
            // Convert YouTube URLs to embed format
            if (videoUrl) {
                if (videoUrl.includes('youtube.com/watch?v=')) {
                    videoUrl = videoUrl.replace('youtube.com/watch?v=', 'youtube.com/embed/');

                } else if (videoUrl.includes('youtu.be/')) {
                    videoUrl = videoUrl.replace('youtu.be/', 'youtube.com/embed/');
                } else if (videoUrl.includes('youtube.com/shorts/')) {
                    // Handle YouTube Shorts URLs
                    videoUrl = videoUrl.replace('youtube.com/shorts/', 'youtube.com/embed/');
                } else if (videoUrl.includes('youtube.com/embed/')) {
                    // Already in embed format
                    videoUrl = videoUrl;
                } else {
                    // Try to extract video ID from various YouTube URL formats
                    const videoIdMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([^&\n?#]+)/);
                    if (videoIdMatch) {
                        videoUrl = `https://www.youtube.com/embed/${videoIdMatch[1]}`;
                    }
                }
            }
            console.log('Single video - Converted URL:', videoUrl);
            
            // Create single video template (only once)
            
            // Use first AI section if available, otherwise use fallback
            const firstSection = aiResponse.sections && Array.isArray(aiResponse.sections) && aiResponse.sections.length > 0 
                ? aiResponse.sections[0] 
                : null;
            
            const template = createSingleVideoTemplate({
                headline: assetsByType.videos[0].title || "Featured Video",
                description: assetsByType.videos[0].description || "Watch this amazing video",
                video: {
                    title: assetsByType.videos[0].title,
                    url: videoUrl,
                    description: assetsByType.videos[0].description
                }
            }, templateIndex);
            
            templates.push(template);
            previewUrls.push({
                section_id: `single_video_${templateIndex}`,
                section_name: "Single Video Display",
                section_type: "video",
                template: template,
                preview_url: `#section_${sectionIndex}_single_video`
            });
            templateIndex++;
            sectionIndex++;
        }
        
        // Create individual single video sections for each video
        if (assetsByType.videos.length > 1) {
            console.log('Creating individual single video sections for', assetsByType.videos.length, 'videos');
            
            // Create single video sections for each video (skip the first one as it's already created above)
            for (let i = 1; i < assetsByType.videos.length; i++) {
                const vid = assetsByType.videos[i];
                
                // Check multiple possible URL fields
                let videoUrl = vid.publicUrl ||vid.fileUrl || vid.url || vid.videoUrl || vid.filePath || vid.imageUrl;
                
                // If we have a direct URL (like YouTube), use it directly
                if (videoUrl && (videoUrl.startsWith('http') || videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'))) {
                    // Use the URL directly for external links
                    console.log('Using direct video URL for single template:', videoUrl);
                } else if (!videoUrl && vid._id) {
                    // If no URL found, try to construct it from asset ID
                    videoUrl = `${baseUrl}/api/assets/file/${vid._id}?shop_domain=${vid.shop_domain || 'default'}`;
                } else if (videoUrl && !videoUrl.startsWith('http')) {
                    // Ensure absolute URL for relative paths
                    if (videoUrl.startsWith('/')) {
                        videoUrl = `${baseUrl}${videoUrl}`;
                    } else {
                        videoUrl = `${baseUrl}/${videoUrl}`;
                    }
                }
                
                console.log(`Single video ${i + 1} - Original URL:`, videoUrl);
           

                videoUrl = convertToYouTubeEmbedUrl(videoUrl);
                console.log(`Single video ${i + 1} - Converted URL:`, videoUrl);
                
                const template = createSingleVideoTemplate({
                    headline: vid.title || `Video ${i + 1}`,
                    description: vid.description || "Watch this amazing video",
                    video: {
                        title: vid.title,
                        url: videoUrl,
                        description: vid.description
                    }
                }, templateIndex);
                
                templates.push(template);
                previewUrls.push({
                    section_id: `single_video_${templateIndex}`,
                    section_name: `Single Video ${i + 1}`,
                    section_type: "video",
                    template: template,
                    preview_url: `#section_${sectionIndex}_single_video`
                });
                templateIndex++;
                sectionIndex++;
            }
        }
        
        // Also create carousel template for 4+ videos (limited to 6 items)
        if (assetsByType.videos.length >= 4) {
            // Video carousel template (4+ videos, limited to 6)
            const carouselVideos = assetsByType.videos.slice(0, 6); // Limit to 6 items
            console.log('Creating video carousel template for', carouselVideos.length, 'videos (limited from', assetsByType.videos.length, ')');
            const template = createVideoCarouselTemplate({
                headline: aiResponse.headline || "Video Collection",
                description: aiResponse.description || "Watch our video collection",
                videos: carouselVideos.map(vid => {
                    console.log('Video asset for carousel:', vid);
                    console.log('Video asset keys:', Object.keys(vid));
                    
                    // Check multiple possible URL fields
                    let videoUrl = vid.publicUrl || vid.fileUrl || vid.url || vid.videoUrl || vid.filePath || vid.imageUrl;
                    
                    // If we have a direct URL (like YouTube), use it directly
                    if (videoUrl && (videoUrl.startsWith('http') || videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'))) {
                        // Use the URL directly for external links
                        console.log('Using direct video URL:', videoUrl);
                    } else if (!videoUrl && vid._id) {
                        // If no URL found, try to construct it from asset ID
                        videoUrl = `${baseUrl}/api/assets/file/${vid._id}?shop_domain=${vid.shop_domain || 'default'}`;
                    } else if (videoUrl && !videoUrl.startsWith('http')) {
                        // Ensure absolute URL for relative paths
                        if (videoUrl.startsWith('/')) {
                            videoUrl = `${baseUrl}${videoUrl}`;
                        } else {
                            videoUrl = `${baseUrl}/${videoUrl}`;
                        }
                    }
                    
                    console.log('Original video URL:', videoUrl);
                    
                    // Convert YouTube URLs to embed format
                    
                    videoUrl = convertToYouTubeEmbedUrl(videoUrl);
                    console.log('Converted video URL:', videoUrl);
                    return {
                        title: vid.title,
                        url: videoUrl,
                        description: vid.description
                    };
                })
            }, templateIndex);
            
            templates.push(template);
            previewUrls.push({
                section_id: `video_carousel_${templateIndex}`,
                section_name: "Video Carousel",
                section_type: "video_carousel",
                template: template,
                preview_url: `#section_${sectionIndex}_video_carousel`
            });
            templateIndex++;
            sectionIndex++;
        }
    }
    
    // 3. PDF Template (FAQ) - Generate 3 sections with 5 Q&A each based on AI response
    if (assetsByType.pdfs.length > 0) {
        if(page=="AssetsSection")
        {

        }
        else
        {
            console.log('Creating PDF FAQ templates for', assetsByType.pdfs.length, 'PDFs');
            console.log('AI Response for PDF FAQ:', aiResponse);
            
            // Parse AI response to extract FAQ data
            let aiFaqData = [];
            let faqHeadline = "Document Information";
            let faqDescription = "Find answers to common questions about the document content";
            
            // Check if AI response contains FAQ data
            if (aiResponse.faq && Array.isArray(aiResponse.faq)) {
                aiFaqData = aiResponse.faq;
                console.log('AI Generated FAQ data:', aiFaqData);
            } else if (typeof aiResponse === 'string') {
                // Try to parse JSON from string response
                try {
                    const parsedResponse = JSON.parse(aiResponse);
                    if (parsedResponse.faq && Array.isArray(parsedResponse.faq)) {
                        aiFaqData = parsedResponse.faq;
                        faqHeadline = parsedResponse.headline || faqHeadline;
                        faqDescription = parsedResponse.description || faqDescription;
                        console.log('Parsed FAQ data from JSON string:', aiFaqData);
                    }
                } catch (e) {
                    console.log('Could not parse AI response as JSON:', e);
                }
            }
            
            // Generate one FAQ section per PDF
            const numberOfSections = Math.max(3, assetsByType.pdfs.length); // At least 3 sections, or one per PDF
            for (let sectionIndex = 0; sectionIndex < numberOfSections; sectionIndex++) {
                let faqData = [];
                let sectionHeadline = `${faqHeadline} - Section ${sectionIndex + 1}`;
                let sectionDescription = `${faqDescription} (Part ${sectionIndex + 1})`;
                
                // Use PDF title if available for better user understanding
                if (assetsByType.pdfs[sectionIndex]) {
                    const pdfAsset = assetsByType.pdfs[sectionIndex];
                    sectionHeadline = `FAQ for ${pdfAsset.title}`;
                    sectionDescription = `Frequently asked questions about ${pdfAsset.title}`;
                }
                
                // Check if we have PDF-specific data from AI response
                if (aiResponse && aiResponse.pdf_faq_sections && aiResponse.pdf_faq_sections[sectionIndex]) {
                    const pdfSection = aiResponse.pdf_faq_sections[sectionIndex];
                    faqData = pdfSection.faq || [];
                    sectionHeadline = pdfSection.headline || pdfSection.pdf_title || sectionHeadline;
                    sectionDescription = pdfSection.description || sectionDescription;
                    console.log(`Using PDF-specific data for section ${sectionIndex + 1}:`, pdfSection);
                } else if (aiFaqData.length > 0) {
                    // Use AI-generated FAQ data - distribute evenly across all sections
                    const questionsPerSection = Math.max(5, Math.floor(aiFaqData.length / numberOfSections));
                    const startIndex = sectionIndex * questionsPerSection;
                    const endIndex = Math.min(startIndex + questionsPerSection, aiFaqData.length);
                    faqData = aiFaqData.slice(startIndex, endIndex);
                    
                    // If this is the last section and there are remaining questions, include them
                    if (sectionIndex === numberOfSections - 1 && endIndex < aiFaqData.length) {
                        const remainingQuestions = aiFaqData.slice(endIndex);
                        faqData = [...faqData, ...remainingQuestions];
                    }
                    
                    console.log(`FAQ data from AI for section ${sectionIndex + 1}:`, faqData);
                }
                
                // If not enough FAQ data from AI, use default questions
                if (faqData.length < 5) {
                    // Update section description to include PDF title for context
                    if (assetsByType.pdfs[sectionIndex]) {
                        const pdfAsset = assetsByType.pdfs[sectionIndex];
                        sectionDescription = `Common questions and answers about ${pdfAsset.title}`;
                    }
                    
                    // Get PDF-specific context for default questions
                    const pdfTitle = assetsByType.pdfs[sectionIndex] ? assetsByType.pdfs[sectionIndex].title : "this document";
                    
                    const defaultFaqSections = [
                        [
                            {
                                question: `What is the main purpose of ${pdfTitle}?`,
                                answer: `${pdfTitle} provides comprehensive information and guidelines related to your specific query, offering detailed insights and practical solutions for implementation.`
                            },
                            {
                                question: `How can I effectively use the information in ${pdfTitle}?`,
                                answer: `Follow the step-by-step instructions provided in ${pdfTitle}, pay attention to the detailed explanations, and implement the recommendations systematically for optimal results.`
                            },
                            {
                                question: `What are the key benefits mentioned in ${pdfTitle}?`,
                                answer: `${pdfTitle} outlines significant advantages including improved efficiency, better understanding of processes, and practical solutions that can be immediately applied.`
                            },
                            {
                                question: `Are there any important considerations when using ${pdfTitle}?`,
                                answer: `Yes, please review all safety guidelines in ${pdfTitle}, follow the recommended procedures carefully, and ensure you understand all prerequisites before implementation.`
                            },
                            {
                                question: `Where can I find additional support for ${pdfTitle}?`,
                                answer: `For further assistance with ${pdfTitle}, refer to the contact information provided in the document, check the reference materials, or reach out to the support team for clarification.`
                            }
                        ],
                        [
                            {
                                question: `What are the implementation steps for ${pdfTitle}?`,
                                answer: `The implementation process for ${pdfTitle} involves several key phases: planning and preparation, systematic execution, monitoring progress, and continuous improvement based on results and feedback.`
                            },
                            {
                                question: `What resources are required for ${pdfTitle}?`,
                                answer: `You'll need access to the necessary tools, adequate time allocation, team coordination, and potentially additional training or support materials to ensure successful implementation of ${pdfTitle}.`
                            },
                            {
                                question: `How long does the ${pdfTitle} process take?`,
                                answer: `The timeline for ${pdfTitle} varies depending on complexity and scope, but typically ranges from a few weeks to several months, with regular checkpoints to assess progress and make adjustments.`
                            },
                            {
                                question: `What are the expected outcomes from ${pdfTitle}?`,
                                answer: `Expected outcomes from ${pdfTitle} include improved efficiency, better results, enhanced understanding, and the ability to replicate successful processes in future projects or situations.`
                            },
                            {
                                question: `How do I measure success with ${pdfTitle}?`,
                                answer: `Success with ${pdfTitle} can be measured through key performance indicators, feedback from stakeholders, comparison with baseline metrics, and achievement of predefined goals and objectives.`
                            }
                        ],
                        [
                            {
                                question: "What are the common challenges?",
                                answer: "Common challenges include resource constraints, time limitations, resistance to change, technical difficulties, and the need for ongoing training and support throughout the process."
                            },
                            {
                                question: "How can I overcome obstacles?",
                                answer: "Overcome obstacles by maintaining clear communication, seeking support when needed, breaking down complex tasks into manageable steps, and staying flexible to adapt as needed."
                            },
                            {
                                question: "What best practices should I follow?",
                                answer: "Best practices include thorough planning, regular communication, documentation of processes, continuous learning, and maintaining a focus on quality and efficiency throughout implementation."
                            },
                            {
                                question: "How do I ensure sustainability?",
                                answer: "Ensure sustainability by building internal capabilities, creating documentation, training team members, establishing monitoring systems, and planning for long-term maintenance and improvement."
                            },
                            {
                                question: "What's next after completion?",
                                answer: "After completion, focus on evaluation, documentation of lessons learned, planning for future improvements, sharing knowledge with others, and applying insights to new projects or challenges."
                            }
                        ],
                        [
                            {
                                question: "What are the technical requirements?",
                                answer: "Technical requirements include proper system specifications, compatible software versions, adequate hardware resources, network connectivity, and security protocols to ensure optimal performance and functionality."
                            },
                            {
                                question: "How do I handle data management?",
                                answer: "Data management involves establishing clear protocols for collection, storage, processing, and analysis, ensuring data integrity, privacy protection, and compliance with relevant regulations and standards."
                            },
                            {
                                question: "What training is required?",
                                answer: "Training requirements include understanding the system functionality, learning proper procedures, developing troubleshooting skills, and staying updated with new features and best practices."
                            },
                            {
                                question: "How do I monitor progress?",
                                answer: "Monitor progress through regular checkpoints, performance metrics, feedback collection, milestone tracking, and continuous evaluation to ensure goals are being met effectively."
                            },
                            {
                                question: "What are the quality standards?",
                                answer: "Quality standards include accuracy, completeness, timeliness, consistency, and adherence to established protocols to ensure reliable and professional outcomes."
                            }
                        ],
                        [
                            {
                                question: "How do I handle user feedback?",
                                answer: "Handle user feedback by establishing clear channels for input, responding promptly to concerns, implementing improvements based on suggestions, and maintaining open communication throughout the process."
                            },
                            {
                                question: "What documentation is needed?",
                                answer: "Documentation needs include user guides, technical specifications, process workflows, troubleshooting guides, and regular updates to ensure comprehensive and current information."
                            },
                            {
                                question: "How do I ensure security?",
                                answer: "Ensure security through proper access controls, data encryption, regular security updates, user authentication, and compliance with security best practices and regulations."
                            },
                            {
                                question: "What are the maintenance requirements?",
                                answer: "Maintenance requirements include regular updates, system monitoring, performance optimization, backup procedures, and ongoing support to ensure continued functionality."
                            },
                            {
                                question: "How do I scale the solution?",
                                answer: "Scale the solution by planning for growth, designing flexible architecture, implementing modular components, and establishing processes that can adapt to increasing demands."
                            }
                        ],
                        [
                            {
                                question: "What are the integration options?",
                                answer: "Integration options include API connections, data synchronization, third-party tool compatibility, and seamless workflow integration to enhance functionality and efficiency."
                            },
                            {
                                question: "How do I handle errors?",
                                answer: "Handle errors by implementing robust error handling, logging systems, user-friendly error messages, recovery procedures, and support processes for quick resolution."
                            },
                            {
                                question: "What are the performance metrics?",
                                answer: "Performance metrics include response times, accuracy rates, user satisfaction scores, system uptime, and efficiency indicators to measure success and identify areas for improvement."
                            },
                            {
                                question: "How do I ensure compliance?",
                                answer: "Ensure compliance by understanding relevant regulations, implementing necessary controls, conducting regular audits, maintaining documentation, and staying updated with legal requirements."
                            },
                            {
                                question: "What support is available?",
                                answer: "Support is available through multiple channels including documentation, help desk services, community forums, training resources, and direct assistance from technical teams."
                            }
                        ]
                    ];
                    
                    // Use different default questions for each section
                    faqData = defaultFaqSections[sectionIndex] || defaultFaqSections[0];
                    console.log(`FAQ using default data for section ${sectionIndex + 1}:`, faqData);
                }
                
                const template = createFAQTemplate({
                    headline: sectionHeadline,
                    description: sectionDescription,
                    faq: faqData
                }, templateIndex);
                
                templates.push(template);
                previewUrls.push({
                    section_id: `faq_${templateIndex}`,
                    section_name: sectionHeadline,
                    section_type: "faq",
                    template: template,
                    preview_url: `#section_${sectionIndex}_faq`
                });
                templateIndex++;
            }
        }
    }
    
    // 4. Contact Template
    if (assetsByType.contacts.length > 0) {
        const template = createContactTemplate({
            headline: aiResponse.headline || "Contact Information",
            description: aiResponse.description || "Get in touch with us",
            contacts: assetsByType.contacts.map(contact => ({
                title: contact.title,
                description: contact.description,
                contact: contact.url
            }))
        }, templateIndex);
        
        templates.push(template);
        previewUrls.push({
            section_id: `contact_${templateIndex}`,
            section_name: "Contact Information",
            section_type: "contact",
            template: template,
            preview_url: `#section_${sectionIndex}_contact`
        });
        templateIndex++;
        sectionIndex++;
    }
    
    // Return both templates and preview URLs
    return {
        templates: templates,
        previewUrls: previewUrls,
        totalSections: sectionIndex - 1
    };
};
     /////////////////////Helper function createVideo url ////////////
     function convertToYouTubeEmbedUrl(videoUrl) {
        if (!videoUrl || typeof videoUrl !== 'string') return '';
    
        try {
            const url = new URL(videoUrl);
    
            let videoId = '';
            let playlistId = url.searchParams.get('list');
    
            if (url.hostname.includes('youtu.be')) {
                videoId = url.pathname.slice(1);
            } else if (url.hostname.includes('youtube.com')) {
                if (url.pathname.includes('/watch')) {
                    videoId = url.searchParams.get('v');
                } else if (url.pathname.includes('/shorts/')) {
                    const parts = url.pathname.split('/');
                    videoId = parts[parts.indexOf('shorts') + 1];
                } else if (url.pathname.includes('/embed/')) {
                    return videoUrl; // Already embedded
                }
            }
    
            if (videoId) {
                return `https://www.youtube.com/embed/${videoId}${playlistId ? `?list=${playlistId}` : ''}`;
            }
        } catch (err) {
            console.warn('Invalid YouTube URL:', videoUrl);
        }
    
        return videoUrl;
    }
    ////////////////////////////Helper function createVideo url ////////////
    
export default {
    createSingleImageTemplate,
    createImageCarouselTemplate,
    createSingleVideoTemplate,
    createVideoCarouselTemplate,
    createSingleMediaTemplate,
    createFAQTemplate,
    createTemplate5,
    createContactTemplate,
    createMixedMediaTemplate,
    processAssetsAndGenerateTemplates,
    getMinimalSectionCSS
};